/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    arrayOf,
    bool,
    func,
    node,
    number,
    oneOf,
    shape,
    string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import ProductFacetItem from './ProductFacetItem';

const useStyles = makeStyles(() => ({
    paper: {
        position: 'absolute',
        bottom: 0,
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
    },
    header: {
        padding: '18px 16px',
        borderBottom: '1px solid #E5E5E5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    headerSide: {
        minWidth: '48px',
        display: 'flex',
        alignItems: 'center',
    },
    headerSideRight: {
        justifyContent: 'flex-end',
    },
    titleWrap: {
        flex: 1,
        textAlign: 'center',
    },
    title: {
        fontSize: '16px',
        fontWeight: 600,
    },
    closeBtn: {
        padding: '0px',
    },
    clearAll: {
        border: 'none',
        background: 'transparent',
        textDecoration: 'underline',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        color: '#000000',
    },
    body: {
        maxHeight: '60vh',
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    footer: {
        borderTop: '1px solid #E5E5E5',
        padding: '12px',
    },
    fullButton: {
        width: '100%',
        textTransform: 'none',
        fontFamily: 'Lato, sans-serif',
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: '26px',
        letterSpacing: '0.72px',
        borderRadius: '8px',
        padding: '15px 28px',
    },
    trigger: {
        border: 'none',
        padding: 0,
        margin: 0,
        background: 'transparent',
        textAlign: 'inherit',
        cursor: 'pointer',
    },
}));

const ProductFacetMobileSheet = ({
    title,
    entries,
    selectedValues,
    onApply,
    resultsCount,
    children,
    itemType,
    stateless,
    trackEvent,
    loading,
}) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [localSelectedValues, setLocalSelectedValues] = useState(selectedValues);
    const prevOpenRef = useRef(false);

    useEffect(() => {
        if (!stateless && open && !prevOpenRef.current) {
            setLocalSelectedValues(selectedValues);
        }
        prevOpenRef.current = open;
    }, [open, selectedValues, stateless]);

    const selectedFacetValues = stateless ? selectedValues : localSelectedValues;

    const showResultsText = resultsCount !== null && resultsCount !== undefined
        ? `Show results (${resultsCount} ${resultsCount === 1 ? 'item' : 'items'})`
        : 'Show results';

    const handleOnClick = () => {
        setOpen(true);
        trackEvent();
    };

    return (
        <>
            <button type="button" className={classes.trigger} onClick={handleOnClick}>
                {children}
            </button>
            <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: classes.paper }}>
                <div className={classes.header}>
                    <div className={classes.headerSide}>
                        <IconButton className={classes.closeBtn} onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className={classes.titleWrap}>
                        <Typography className={classes.title}>{title}</Typography>
                    </div>
                    <div className={`${classes.headerSide} ${classes.headerSideRight}`}>
                        {selectedFacetValues.length > 0 && itemType !== 'radio' && (
                            <button
                                type="button"
                                className={classes.clearAll}
                                onClick={() => {
                                    if (stateless) {
                                        onApply([]);
                                    } else {
                                        setLocalSelectedValues([]);
                                    }
                                }}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                </div>
                <div className={classes.body}>
                    {entries.map((entry) => (
                        <ProductFacetItem
                            key={entry.value}
                            entry={entry}
                            checked={selectedFacetValues.includes(entry.value)}
                            onChange={(value, checked) => {
                                if (stateless) {
                                    if (itemType === 'radio') {
                                        trackEvent('Click', null, value);
                                        onApply(checked ? [value] : []);
                                        return;
                                    }
                                    const nextSelectedValues = checked
                                        ? [...selectedValues, value]
                                        : selectedValues.filter((v) => v !== value);
                                    if (checked) {
                                        trackEvent('Click', { name: entry.name, value, count: entry.count });
                                    }
                                    onApply(nextSelectedValues);
                                    return;
                                }
                                if (itemType === 'radio') {
                                    setLocalSelectedValues(checked ? [value] : []);
                                    trackEvent('Click');
                                    return;
                                }
                                setLocalSelectedValues((prev) => {
                                    if (checked) return [...prev, value];
                                    return prev.filter((v) => v !== value);
                                });
                            }}
                            type={itemType}
                            loading={loading}
                        />
                    ))}
                </div>
                <div className={classes.footer}>
                    <Button
                        className={classes.fullButton}
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            if (!stateless) {
                                onApply(localSelectedValues);
                            }
                            setOpen(false);
                        }}
                    >
                        {showResultsText}
                    </Button>
                </div>
            </Dialog>
        </>
    );
};

ProductFacetMobileSheet.propTypes = {
    title: string.isRequired,
    entries: arrayOf(shape({
        name: string.isRequired,
        value: string.isRequired,
        count: number,
        from: number,
        to: number,
        tooltip: string,
    })).isRequired,
    selectedValues: arrayOf(string),
    onApply: func,
    resultsCount: number,
    children: node.isRequired,
    itemType: oneOf(['checkbox', 'radio']),
    stateless: bool,
    trackEvent: func,
    loading: bool,
};

ProductFacetMobileSheet.defaultProps = {
    selectedValues: [],
    onApply: () => {},
    resultsCount: null,
    itemType: 'checkbox',
    stateless: false,
    trackEvent: () => {},
    loading: false,
};

export default ProductFacetMobileSheet;
